import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { Alert } from 'features/alerts/types/alert'
import { useFeatureFlag } from 'features/user/hooks/useFeatureFlag'
import { useAbility } from 'providers/casl'
import { AdUnitAlerts } from '../../components/AdUnitAlerts'
import { AddEditAdUnitContext } from '../../contexts/AddEditAdUnitContext'
import { AdUnitAlert } from '../../types/adUnitAlert'
import { useGetFreshAdUnitAlerts } from '../GeneralForm/hooks/useGetFreshAdUnitAlerts'
import { Schema } from './schema'
import { BasicSettingsSection } from './Sections/BasicSettingsSection'

export const Form = (): JSX.Element => {
  const { t } = useTranslation('features/adUnit')
  const { adUnit } = useContext(AddEditAdUnitContext)
  const { control } = useFormContext<Schema>()
  const ability = useAbility()
  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')

  const freshAdUnitAlerts: Alert<AdUnitAlert>[] = useGetFreshAdUnitAlerts({
    adUnitId: adUnit?.id,
  })

  if (adUnit === undefined) {
    throw Error('Ad unit should be accessible')
  }

  const refresherAlerts: Alert<AdUnitAlert>[] = freshAdUnitAlerts.filter(
    (alert: Alert<AdUnitAlert>): boolean => alert.name.startsWith('refresher'),
  )

  const { isFeatureEnabled: withControllingRefreshParamsOnAdUnitLevel } = useFeatureFlag(
    'yp2827ControlingRefreshParamsOnAdUnitLevel',
  )

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('form.refresher.enabled.description')}
          title={t('form.refresher.enabled.title')}
          titleEndAdornment={
            <FlexBox axis='x'>
              <Switch
                control={control}
                disabled={cannotUpdateAdUnit}
                name='refresherEnabled'
              />

              {refresherAlerts.length > 0 && <AdUnitAlerts alerts={refresherAlerts} />}
            </FlexBox>
          }
        />
      </Paper>

      {withControllingRefreshParamsOnAdUnitLevel && <BasicSettingsSection />}
    </>
  )
}
